/**
 * Plan 10, Task 5.1 — `createRenderer()` factory + render loop.
 *
 * Composes the 6 renderer subsystems (scene / camera / lighting / composer /
 * perf-stats / debug-overlay) and drives them via a requestAnimationFrame loop.
 *
 * Per Plan 10 Task 5.1 (plan lines 1031-1086):
 *
 *   1. const now = config.now()
 *   2. const dt = (now - lastTick) / 1000
 *   3. cameraRig.update(dt)
 *   4. composer.render(scene.scene, cameraRig.camera)
 *   5. const stats = perfStats.tick()
 *   6. debugOverlay.update(stats)
 *
 * On the FIRST successful tick, sets `(globalThis as any).__renderReady = true`.
 * This is the contract that the puppeteer fixture (Task 6.1) waits on:
 * `tools/headless-render.js:45` → `page.waitForFunction(() => window.__renderReady === true)`.
 *
 * Reverse-construction dispose order (plan line 1102):
 *   loop → debug-overlay → perf-stats → composer → lighting → cameraRig → scene
 *
 * Construction order in the factory (must mirror the reverse-dispose chain):
 *   scene → camera → lighting → composer → perf-stats → debug-overlay → loop
 */

import { createScene, type SceneConfig } from './scene';
import { createCameraRig } from './camera';
import { createLightingRig, type LightingConfig } from './lighting';
import { createComposerMount } from './composer-mount';
import { createPerfStats } from './perf-stats';
import { createDebugOverlay, type DebugOverlayConfig } from './debug-overlay';
import type {
  SceneHandle,
  CameraRig,
  LightingRig,
  DebugOverlayHandle,
  ComposerMount,
  RenderLoopHandle,
  PerfStats,
  CameraRigConfig,
} from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Public API
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Composite handle returned by `createRenderer()`. Owns lifecycle of every
 * renderer subsystem AND the rAF loop.
 *
 * @see Plan 10 Task 5.1 lines 1045-1057
 */
export interface RendererHandle {
  readonly scene: SceneHandle;
  readonly cameraRig: CameraRig;
  readonly lighting: LightingRig;
  readonly composer: ComposerMount;
  readonly debugOverlay: DebugOverlayHandle;
  readonly loop: RenderLoopHandle;
  /** Convenience snapshot getter; reads from the perf-stats collector. */
  readonly stats: () => PerfStats;
  /** Idempotent — disposes every subsystem in reverse-construction order. */
  dispose(): void;
}

/**
 * Configuration for `createRenderer()`. Test injectables are first-class and
 * defaulted to production globals (`requestAnimationFrame`, `cancelAnimationFrame`,
 * `performance.now`).
 */
export interface RendererConfig {
  /** The HTMLCanvasElement to render into. Required. */
  readonly canvas: HTMLCanvasElement;
  /** Initial size. Default: canvas.clientWidth/Height (or 1280×720 fallback). */
  readonly width?: number;
  readonly height?: number;
  /**
   * Test-only: substitute requestAnimationFrame.
   * Default: `globalThis.requestAnimationFrame.bind(globalThis)`.
   */
  readonly rafSchedule?: (cb: FrameRequestCallback) => number;
  /**
   * Test-only: substitute cancelAnimationFrame.
   * Default: `globalThis.cancelAnimationFrame.bind(globalThis)`.
   */
  readonly cancelRaf?: (id: number) => void;
  /** Test-only: substitute the time source. Default: `() => performance.now()`. */
  readonly now?: () => number;
  /**
   * Pass-through to `createScene(canvas, sceneConfig)`. Tests use this to
   * inject `rendererFactory` (the stub WebGLRenderer factory) so they can run
   * in happy-dom without a real WebGL context.
   */
  readonly sceneConfig?: SceneConfig;
  /** Pass-through to `createCameraRig(cameraConfig)`. */
  readonly cameraConfig?: CameraRigConfig;
  /** Pass-through to `createLightingRig(scene, lightingConfig)`. */
  readonly lightingConfig?: LightingConfig;
  /** Pass-through to `createDebugOverlay(debugOverlayConfig)`. */
  readonly debugOverlayConfig?: DebugOverlayConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default fallback size (when canvas has no clientWidth/Height yet, e.g. in
// happy-dom or pre-mount). Matches the puppeteer fixture's default canvas.
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_FALLBACK_WIDTH = 1280;
const DEFAULT_FALLBACK_HEIGHT = 720;

// ─────────────────────────────────────────────────────────────────────────────
// Implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compose the 6 renderer subsystems and drive them with a rAF loop.
 *
 * @param config - Configuration. Only `canvas` is required.
 * @returns A `RendererHandle` whose `dispose()` tears down every subsystem.
 *
 * @example
 *   const handle = createRenderer({ canvas });
 *   handle.loop.start();
 *   // … later …
 *   handle.loop.stop();
 *   handle.dispose();
 */
export function createRenderer(config: RendererConfig): RendererHandle {
  // ── Resolve injectables ────────────────────────────────────────────────────
  const rafSchedule: (cb: FrameRequestCallback) => number =
    config.rafSchedule ?? globalThis.requestAnimationFrame.bind(globalThis);
  const cancelRaf: (id: number) => void =
    config.cancelRaf ?? globalThis.cancelAnimationFrame.bind(globalThis);
  const now: () => number = config.now ?? (() => performance.now());

  // Initial size — prefer config, then canvas client rect, then fallback.
  // clientWidth/Height are always numbers (0 if unattached), so we can't use
  // ?? to fall through; we explicitly guard for >0 before falling to the default.
  const initialWidth = config.width ?? config.canvas.clientWidth;
  const initialHeight = config.height ?? config.canvas.clientHeight;
  const width = initialWidth > 0 ? initialWidth : DEFAULT_FALLBACK_WIDTH;
  const height = initialHeight > 0 ? initialHeight : DEFAULT_FALLBACK_HEIGHT;

  // ── Subsystem construction (forward order) ────────────────────────────────
  const scene = createScene(config.canvas, config.sceneConfig);
  scene.onResize(width, height);

  const cameraRig = createCameraRig(config.cameraConfig);
  cameraRig.onResize(width, height);

  const lighting = createLightingRig(scene.scene, config.lightingConfig);

  const composer = createComposerMount(
    {
      renderer: scene.renderer,
      postprocessing: {
        usePostprocessing: true,
        bloom: true,
        smaa: false,
        chromaticAberration: false,
      },
    },
    scene.scene,
    cameraRig.camera,
  );
  composer.onResize(width, height);

  const perfStats = createPerfStats({
    renderer: scene.renderer,
    now,
  });

  const debugOverlay = createDebugOverlay(config.debugOverlayConfig);

  // ── Loop state ─────────────────────────────────────────────────────────────
  let running = false;
  let rafId: number | null = null;
  let lastTickMs: number | null = null;
  let firstTickComplete = false;

  /**
   * Per-frame callbacks registered via `loop.addFrameTickCallback`.
   * Using `Set` gives O(1) insertion, O(1) deletion (Set.delete), and
   * insertion-order iteration (ECMA-262 § 23.2.3.6) which preserves
   * registration order for predictable per-frame sequencing.
   *
   * Plan 21a Task 10.1: callbacks fire BEFORE cameraRig.update(dt).
   */
  const frameTickCallbacks = new Set<(dt: number) => void>();

  /** Single rAF callback — the loop body per plan lines 1077-1084. */
  function tick(): void {
    // If stop() was called before this rAF fired, bail out.
    if (!running) return;

    const nowMs = now();

    // Compute delta seconds. First tick: lastTickMs is null → dt=0 (not negative).
    const dt = lastTickMs === null ? 0 : (nowMs - lastTickMs) / 1000;
    lastTickMs = nowMs;

    // Plan 21a Task 10.1: fire registered per-frame callbacks BEFORE cameraRig.update.
    for (const cb of frameTickCallbacks) cb(dt);

    // 3. cameraRig.update(dt)
    cameraRig.update(dt);

    // 4. composer.render(scene.scene, cameraRig.camera)
    composer.render(scene.scene, cameraRig.camera);

    // 5. const stats = perfStats.tick()
    const stats = perfStats.tick();

    // 6. debugOverlay.update(stats)
    debugOverlay.update(stats);

    // First-tick contract: signal that the first frame has rendered.
    // tools/headless-render.js:45 polls for this via page.waitForFunction.
    if (!firstTickComplete) {
      firstTickComplete = true;
      (globalThis as { __renderReady?: boolean }).__renderReady = true;
    }

    // Schedule the next tick.
    rafId = rafSchedule(tick);
  }

  const loop: RenderLoopHandle = {
    start(): void {
      if (running) return;
      running = true;
      rafId = rafSchedule(tick);
    },

    stop(): void {
      if (!running) return;
      running = false;
      if (rafId !== null) {
        cancelRaf(rafId);
        rafId = null;
      }
    },

    get isRunning(): boolean {
      return running;
    },

    dispose(): void {
      // Idempotent. stop() is itself idempotent.
      this.stop();
      // Clear all per-frame callbacks so they cannot fire if the loop is
      // somehow re-driven after dispose. Idempotent: Set.clear() on an
      // already-empty Set is a safe no-op.
      frameTickCallbacks.clear();
    },

    addFrameTickCallback(cb: (dt: number) => void): () => void {
      frameTickCallbacks.add(cb);
      // Return an idempotent unsubscribe function. Set.delete returns false if
      // the item is already absent — no throw, no side-effect.
      return (): void => {
        frameTickCallbacks.delete(cb);
      };
    },
  };

  // ── Composite handle dispose ──────────────────────────────────────────────
  let composeDisposed = false;

  function dispose(): void {
    if (composeDisposed) return;
    composeDisposed = true;

    // Reverse-construction order per Plan 10 Task 5.1 spec line 1102.
    // Construction order: scene → camera → lighting → composer → perfStats → debugOverlay → loop
    // Dispose order:      loop → debugOverlay → perfStats → composer → lighting → camera → scene
    loop.dispose();
    debugOverlay.dispose();
    perfStats.dispose();
    composer.dispose();
    lighting.dispose();
    cameraRig.dispose();
    scene.dispose();
  }

  return {
    scene,
    cameraRig,
    lighting,
    composer,
    debugOverlay,
    loop,
    stats: () => perfStats.snapshot(),
    dispose,
  };
}
